/**
 * useSystemSectionCapabilities
 *
 * Runtime presence gates for two System Dashboard sections that are neither a
 * feature flag nor a role: Fail2Ban (only meaningful once the package is
 * installed) and Software RAID (only when at least one array exists). The nav
 * children and search rows for those sections gate on these via the
 * `fail2ban-installed` / `raid-present` capability gates, so an admin on a box
 * without fail2ban or without any RAID array never sees a dead entry.
 *
 * Sources (both admin-only server-side):
 *   - `GET /api/admin/security/fail2ban/summary` → `{ ok, data: { installed } }`
 *   - `GET /api/system/raid/presence`            → `{ ok, data: { present } }`
 *
 * ONE fetch is shared across every consumer via a module-level cache +
 * in-flight promise (both useVisibleSections and useAppSearch call this hook,
 * so a naive per-hook fetch would double every load). The cache refreshes at
 * most every 5 minutes and whenever the tab returns to the foreground.
 *
 * Hidden-until-loaded: `loaded` is false until the first fetch resolves, and
 * both flags default false. Callers gate on `loaded && flag`, so a gated entry
 * is briefly absent rather than flashing in and then vanishing — the same
 * posture as the AMC capability gate in useVisibleSections.
 *
 * Non-admins never fetch: the hook returns the empty (all-false, not-loaded)
 * shape immediately, so no admin-only endpoint is hit for a regular user.
 */
import { useEffect, useState } from "react";
import { useMe } from "./useMe.js";
import { isAdminUser } from "../lib/permissions.js";

export interface SystemSectionCapabilities {
  fail2banInstalled: boolean;
  raidPresent: boolean;
  /** True once the first fetch has resolved (success OR handled failure). */
  loaded: boolean;
}

const EMPTY: SystemSectionCapabilities = {
  fail2banInstalled: false,
  raidPresent: false,
  loaded: false,
};

const REFRESH_MS = 5 * 60 * 1000;

// Module-level singleton state shared across all consumers.
let cache: SystemSectionCapabilities | null = null;
let lastFetched = 0;
let inFlight: Promise<SystemSectionCapabilities> | null = null;
const subscribers = new Set<(caps: SystemSectionCapabilities) => void>();

function notify(caps: SystemSectionCapabilities): void {
  for (const sub of subscribers) sub(caps);
}

async function fetchBool(
  url: string,
  pick: (data: Record<string, unknown>) => unknown,
): Promise<boolean> {
  try {
    const res = await fetch(url, { credentials: "include" });
    const json = (await res.json().catch(() => null)) as
      | { ok: true; data: Record<string, unknown> }
      | { ok: false; message?: string }
      | null;
    if (!res.ok || !json?.ok) return false;
    return pick(json.data) === true;
  } catch {
    return false;
  }
}

async function doFetch(): Promise<SystemSectionCapabilities> {
  const [fail2banInstalled, raidPresent] = await Promise.all([
    fetchBool("/api/admin/security/fail2ban/summary", (d) => d.installed),
    fetchBool("/api/system/raid/presence", (d) => d.present),
  ]);
  const result: SystemSectionCapabilities = { fail2banInstalled, raidPresent, loaded: true };
  cache = result;
  lastFetched = Date.now();
  notify(result);
  return result;
}

function ensureFetch(force: boolean): Promise<SystemSectionCapabilities> {
  const isFresh = cache !== null && Date.now() - lastFetched < REFRESH_MS;
  if (!force && isFresh) return Promise.resolve(cache as SystemSectionCapabilities);
  if (inFlight) return inFlight;
  inFlight = doFetch().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

export const useSystemSectionCapabilities = (): SystemSectionCapabilities => {
  const { user } = useMe();
  const isAdmin = isAdminUser(user);
  const [state, setState] = useState<SystemSectionCapabilities>(() =>
    isAdmin ? cache ?? EMPTY : EMPTY,
  );

  useEffect(() => {
    if (!isAdmin) {
      setState(EMPTY);
      return;
    }

    const sub = (caps: SystemSectionCapabilities) => setState(caps);
    subscribers.add(sub);
    if (cache) setState(cache);
    void ensureFetch(false);

    const interval = window.setInterval(() => void ensureFetch(true), REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void ensureFetch(true);
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      subscribers.delete(sub);
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [isAdmin]);

  return isAdmin ? state : EMPTY;
};
